import { useEffect, useState } from "react";
import { Box, Grid, Stack } from "@mui/material";
import InfoSubTitle from "../../../common/InfoSubTitle";

import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { IUser } from "@/Common/interfaces/user";
import classes from "@/assets/styles/settings/systemSettingsComponent.module.css";
import uniqBy from "lodash-es/uniqBy";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import { IWashoutReviewersObject } from "@/Settings/types/Common/model";
import { IDepartmentHierarchy } from "@/Settings/types/SystemSettings/washoutReviewers";
import { filteredFindSupervisorPositions } from "@/Settings/helpers/SystemSettings/washoutReviewers/findSupervisorPositions";
import checkIsSupervisor from "@/Settings/helpers/SystemSettings/washoutReviewers/checkIsSupervisor";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

type TReviewerSelectorProps = {
	title: string;
	users: IUser[];
	disabled: boolean;
	uniqueKey: string;
	washoutReviewer?: IWashoutReviewersObject;
	departmentHierarchy?: IDepartmentHierarchy;
	onUpdate: (params: { selected: IUser | null; isDirectSupervisor: boolean; position: string }) => void;
};

const otherUsersLabel = "Other Users";

const getFilteredUsers = (users: unknown[] = [], isDirectSupervisor = false) => {
	if (!isDirectSupervisor) {
		return users;
	}
	return users.filter((user) => user?.["group" as keyof object] === "Direct Supervisors");
};

const ReviewerSelector = (props: TReviewerSelectorProps) => {
	const { title, users = [], disabled, uniqueKey, washoutReviewer, onUpdate, departmentHierarchy } = props;
	const [isDirectSupervisor, setIsDirectSupervisor] = useState(false);
	const [warningMessage, setWarningMessage] = useState(false);

	const supervisorsPositions = filteredFindSupervisorPositions(title, departmentHierarchy);

	const finalUsers = uniqBy(
		users.map((user) => {
			const isSupervisor = checkIsSupervisor(supervisorsPositions, user.position);
			return { ...user, label: user.fullName, group: isSupervisor ? "Direct Supervisors" : otherUsersLabel };
		}),
		"_id"
	).sort((a, b) => (a.group > b.group ? 1 : -1));

	const handleSwitch = () => {
		setIsDirectSupervisor((prev) => !prev);
	};

	const handleChange = (selected: IUser | null) => {
		onUpdate({ selected, isDirectSupervisor, position: title });
	};

	useEffect(() => {
		if (washoutReviewer) {
			const { reviewerId } = washoutReviewer;
			setIsDirectSupervisor(washoutReviewer.isDirectSupervisor);
			const user = finalUsers.find((item) => item._id === reviewerId?._id);
			setWarningMessage(reviewerId && !checkIsSupervisor(supervisorsPositions, user?.position));
		}
		return () => {
			setIsDirectSupervisor(false);
			setWarningMessage(false);
		};
	}, [washoutReviewer]);

	const filteredUsers = getFilteredUsers(finalUsers, isDirectSupervisor);

	return (
		<Box className="flex flex-col w-[40%] mr-[100px]">
			<InfoSubTitle title={title} className="!mb-[12px] !mt-[32px]" />
			<Stack direction={"row"} spacing={1}>
				<Stack width={"100%"}>
					<SmartSelector
						key={uniqueKey}
						showBy={"fullName"}
						options={filteredUsers as object[]}
						selected={washoutReviewer?.reviewerId ?? null}
						clearable
						inputProps={{
							placeholder: "Select Supervisor",
						}}
						groupingProps={{
							groupBy: "group",
							defaultLabel: "Unknown",
							sticky: true,
							specificProperties: {
								"Direct Supervisors": {
									classes: "!text-primary",
								},
								"Other Users": {
									classes: "!text-neutral-gray",
									icon: <WarningAmberRoundedIcon className="!mt-0 !w-[15px] !text-neutral-gray" />,
								},
							},
						}}
						listBoxProps={{
							sx: { maxHeight: "320px" },
						}}
						disabled={disabled}
						onChangeAction={(value) => handleChange(value as IUser)}
					/>
				</Stack>

				<CustomizedSwitches
					disabled={disabled}
					textRight
					textChecked="Supervisors Only"
					textUnChecked="Supervisors Only"
					onChange={handleSwitch}
					checked={isDirectSupervisor}
				/>
			</Stack>
			{warningMessage && (
				<Grid container direction={"row"}>
					<Grid>
						<WarningAmberRoundedIcon
							className={`${classes.colorLabelOtherUsers}`}
							style={{ width: "20px", paddingLeft: "2px", paddingBottom: "6px" }}
						/>
					</Grid>
					<Grid className={classes.errorMessage}>Reviewer is not a direct supervisor</Grid>
				</Grid>
			)}
		</Box>
	);
};

export default ReviewerSelector;
